import Head from "next/head";
import { Container, Typography, Box, Grid, Divider, Paper } from "@mui/material";

import MainLayout from "../../ui/layouts/main";

import { PropertyPageSkeleton } from "../../ui/components/property/PropertyPageSkeleton";
import { PropertyPhoto } from "../../ui/components/property/PropertyPhoto";
import { UnitGroupAttributes } from "../../ui/components/UnitGroupAttributes/UnitGroupAttributes";
import { useAttributeMapping } from "../../hooks/useAttributeMapping";

import { UnitGroupListWidgetLazy } from "../../ui/widgets/UnitGroupListWidget/UnitGroupListWidgetLazy";
import { ReactElement, useMemo } from "react";
import { useRecoilValue } from "recoil";
import { searchFormSelector } from "../../state/search-state";
import { OffersListWidgetLazy } from "../../ui/widgets/OffersListWidget/OffersListWidgetLazy";
import FormSearch from "../../ui/components/FormSearch/FormSearch";
import { useTranslation } from "../../fe-ui/hooks/use-translation";

import { composeServerSideProps } from "next-composition";
import { withTranslations } from "../../server/ssrprops/withTranslations";
import { withAttributesSsrProps } from "../../server/ssrprops/withAttributesSsrProps";
import { withSingleHotelSsrProps } from "../../server/ssrprops/withSingleHotelSsrProps";
import dataTestIds from "../../utils/data-test-ids";
import { withBannersSearchSsrProps } from "../../server/ssrprops/withBannerSearchSsrProps";
import { withSeoSsrProps } from "../../server/ssrprops/withSeoSsrProps";
import { MapWithSignlePin } from "../../ui/components/MapWithSignlePin/MapWithSignlePin";
import { BannerSearchListResponseDataItem } from "../../api/content-client/service";
import { ExtendedHotelDto } from "../../api/aggregation-client/service";

type HotelSinglePageProps = {
  bannersSearch: BannerSearchListResponseDataItem[];
  hotel?: ExtendedHotelDto;
};

const Error = () => {
  return <Typography>{"Error"}</Typography>;
};

const HotelSinglePage = ({ bannersSearch, hotel }: HotelSinglePageProps) => {
  const hotelId = hotel?.id;
  const propertyHotel = hotel;
  const propertyHotelIsLoading = false;
  const propertyHotelIsError = false;

  const t = useTranslation();

  const allAttributes = useAttributeMapping({ hotelAttributes: propertyHotel?.attributes || [] });

  const { dateRange } = useRecoilValue(searchFormSelector);

  const propertyMapProps = useMemo(() => {
    if (!hotel) {
      return null;
    }
    const coordinates: undefined | [number, number] = (hotel?.location?.coordinates as any)?.coordinates;
    if (!coordinates) {
      return null;
    }
    return {
      coordinates: coordinates,
      zoom: 14,
    };
  }, [hotel]);

  if (propertyHotelIsError) {
    return <Error />;
  }

  if (!propertyHotel && propertyHotelIsLoading) {
    return <PropertyPageSkeleton />;
  }

  if (!propertyHotel && !propertyHotelIsLoading) {
    return <Error />;
  }

  const media = propertyHotel?.media.map((item) => ({
    src: item.url,
  }));

  return (
    <>
      <Head>
        <title>
          {t("Index_ourHotels")}
          {" - "}
          {propertyHotel?.title}
          {" | "}
          {propertyHotel?.location.city}
          {" | "}
          {propertyHotel?.location.country}
        </title>
      </Head>

      {propertyHotel && (
        <>
          <Container maxWidth={"lg"}>
            <Typography variant={"h4"} sx={{ marginBottom: 2 }}>
              {propertyHotel.title}
            </Typography>
            <Box sx={{ marginBottom: 4 }}>{media?.length && <PropertyPhoto main={media[0]} thumbs={media} />}</Box>
            <Grid container spacing={2} sx={{ marginBottom: 4 }}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant={"body2"}
                  sx={{ marginBottom: 4 }}
                  dangerouslySetInnerHTML={{
                    __html: `${propertyHotel.description.split(/\n+/g).join("<br/><br/>")}`,
                  }}
                />
                {/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore */}
                <UnitGroupAttributes attributes={allAttributes.hotelAttributes} />
                {propertyMapProps && <MapWithSignlePin {...propertyMapProps} />}
              </Grid>
            </Grid>

            <Divider sx={{ marginBottom: 2 }} />

            <Grid container>
              <Grid item sm={9} xs={12}>
                <Typography variant={"h6"} sx={{ marginBottom: 1, marginLeft: 2 }}>
                  {t("Property_hotelRooms")}
                </Typography>
                <Box mb={2} component={Paper} elevation={4} sx={{ borderRadius: "16px" }}>
                  <FormSearch hotelIdContext={`${hotelId}`} />
                </Box>
              </Grid>
            </Grid>

            <Box mb={4}>
              {!!dateRange[0] && !!dateRange[1] ? (
                <OffersListWidgetLazy
                  bannersSearch={bannersSearch}
                  bannerBlockId="hotelPage"
                  showTotalPrice={false}
                  withGuestsContext
                  hotelIdContext={`${hotelId}`}
                  perPage={4}
                  dataTestId={dataTestIds.PAGES.HOTEL.UNITS_CONTAINER}
                />
              ) : (
                <UnitGroupListWidgetLazy
                  // TODO: to add support for bannersSearch
                  // bannersSearch={bannersSearch}
                  // bannerBlockId="hotelPage"
                  hotelIdContext={`${hotelId}`}
                  perPage={4}
                  dataTestId={dataTestIds.PAGES.HOTEL.UNITS_CONTAINER}
                />
              )}
            </Box>
          </Container>
        </>
      )}
    </>
  );
};

const HotelSinglePageWrapper = ({ bannersSearch, hotel }: HotelSinglePageProps) => {
  return <HotelSinglePage bannersSearch={bannersSearch} hotel={hotel} />;
};

export default HotelSinglePageWrapper;

HotelSinglePageWrapper.getLayout = (page: ReactElement) => <MainLayout> {page} </MainLayout>;

export const getServerSideProps = composeServerSideProps({
  use: [withBannersSearchSsrProps, withTranslations, withAttributesSsrProps, withSingleHotelSsrProps, withSeoSsrProps],
});
